// Custom Checkout handler — Storefront API flow.
// 1. On load: bridge Liquid cart → Storefront cart (cartCreate) → có checkoutUrl LIVE
//    với prefill khi update buyer identity (Shopify one-page checkout 2024+ chỉ
//    chấp nhận prefill qua Storefront API, không qua URL params nữa).
// 2. Validate form fields (email, phone VN, address required).
// 3. Submit: cartBuyerIdentityUpdate + cartAttributesUpdate + cartNoteUpdate.
// 4. Redirect cart.checkoutUrl — full prefill email/phone/address.

(function () {
  const form = document.getElementById("mod-checkout-form");
  if (!form) return;

  /* ────── Storefront API client ────── */
  const SF = window.HPW_STOREFRONT || {};
  let storefrontCart = null; // { id, checkoutUrl }
  let bootstrapError = null;
  let bootstrapDone = false;
  const CART_CACHE_KEY = "hpw_sf_cart_v1";
  const CART_CACHE_TTL_MS = 30 * 60 * 1000; // 30 phút

  /* ────── UI state helpers ────── */
  // Submit button có 4 state: preparing (bootstrap running) | ready (sẵn sàng submit)
  // | loading (mutations chạy) | error (bootstrap fail). State drive label + disabled.
  const submitBtn = form.querySelector(".cco-confirm");
  function setSubmitState(state) {
    if (!submitBtn) return;
    const labels = {
      preparing: "Đang chuẩn bị...",
      ready: submitBtn.dataset.defaultLabel || "Xác nhận đặt hàng",
      loading: submitBtn.dataset.loadingLabel || "Đang xử lý...",
      error: "Không thể xử lý",
    };
    submitBtn.textContent = labels[state] || labels.ready;
    submitBtn.disabled = state !== "ready";
  }
  setSubmitState("preparing"); // chạy ngay khi script load

  // Error banner trên đầu form — dùng cho lỗi bootstrap/network không recover được
  function showErrorBanner(message) {
    let banner = form.querySelector(".cco-error-banner");
    if (!banner) {
      banner = document.createElement("div");
      banner.className = "cco-error-banner";
      form.insertBefore(banner, form.firstChild);
    }
    banner.textContent = message;
    banner.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  function hideErrorBanner() {
    form.querySelector(".cco-error-banner")?.remove();
  }

  /* ────── Retry helper — exponential backoff cho transient network/API failures ────── */
  async function withRetry(fn, attempts = 3) {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try { return await fn(); }
      catch (err) {
        lastErr = err;
        // Không retry nếu lỗi do user input (userErrors) — chỉ retry network/5xx
        const msg = err.message || "";
        if (msg.startsWith("[") || msg.includes("GraphQL:")) throw err;
        if (i === attempts - 1) throw err;
        await new Promise((r) => setTimeout(r, 500 * Math.pow(2, i)));
      }
    }
    throw lastErr;
  }

  async function storefrontFetch(query, variables = {}) {
    if (!SF.token || !SF.domain) {
      throw new Error("Storefront token chưa config — Theme Editor → Custom Checkout → Storefront Access Token.");
    }
    const res = await fetch(
      `https://${SF.domain}/api/${SF.apiVersion || "2026-04"}/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": SF.token,
        },
        body: JSON.stringify({ query, variables }),
      },
    );
    const bodyText = await res.text();
    if (!res.ok) {
      console.error("[storefront] HTTP", res.status, bodyText);
      throw new Error(`Storefront API ${res.status}: ${bodyText.slice(0, 200)}`);
    }
    let json;
    try { json = JSON.parse(bodyText); } catch { throw new Error(`Non-JSON: ${bodyText.slice(0, 200)}`); }
    if (json.errors?.length) {
      console.error("[storefront] GraphQL errors", json.errors);
      throw new Error(`GraphQL: ${json.errors.map((e) => e.message).join("; ")}`);
    }
    return json.data;
  }

  function throwUserErrors(errs, op) {
    if (errs?.length) throw new Error(`[${op}] ${errs.map((e) => e.message).join("; ")}`);
  }

  // Lưu codes ở module scope để re-apply sau cùng (cartBuyerIdentityUpdate
  // trigger eligibility check, có thể remove discount silently).
  let cartDiscountCodes = [];

  /* On page load — bridge Liquid cart (cookie) → Storefront cart, lấy checkoutUrl */
  bootstrapStorefrontCart()
    .then(() => {
      bootstrapDone = true;
      // Sau bootstrap → run validation thay vì force ready
      if (typeof updateSubmitButtonState === "function") updateSubmitButtonState();
      else setSubmitState("ready");
    })
    .catch((err) => {
      bootstrapError = err;
      console.error("[mod-checkout] cart bridge failed:", err.message, err);
      setSubmitState("error");
      showErrorBanner(`Không tải được giỏ hàng (${err.message}). Reload trang để thử lại.`);
    });

  // Signature từ bridge — verify cache vẫn match cart hiện tại
  function bridgeSignature(bridge) {
    const lineSig = (bridge.lines || []).map((l) => `${l.variantId}:${l.quantity}`).sort().join(",");
    const codeSig = [...(bridge.discountCodes || [])].sort().join(",");
    return `${lineSig}|${codeSig}`;
  }

  async function bootstrapStorefrontCart() {
    const bridgeEl = document.getElementById("cco-cart-bridge");
    if (!bridgeEl) throw new Error("Missing cart bridge data");
    const bridge = JSON.parse(bridgeEl.textContent);
    console.log("[mod-checkout] bridge data", bridge);
    if (!bridge.lines?.length) return;
    cartDiscountCodes = bridge.discountCodes || [];

    /* Cache check — reuse Storefront cart từ session cũ nếu bridge signature match
       (tránh tạo cart mới mỗi lần reload). 30 phút TTL. */
    const signature = bridgeSignature(bridge);
    try {
      const cached = JSON.parse(sessionStorage.getItem(CART_CACHE_KEY) || "null");
      if (cached && cached.signature === signature && Date.now() < cached.expires) {
        const verify = await storefrontFetch(
          `query Verify($id: ID!) { cart(id: $id) { id checkoutUrl discountCodes { code applicable } } }`,
          { id: cached.cartId },
        );
        if (verify.cart?.checkoutUrl) {
          storefrontCart = verify.cart;
          console.log("[mod-checkout] Storefront cart reused from cache", storefrontCart);
          return;
        }
      }
    } catch { /* cache corrupted, ignore + fresh create */ }

    /* Fresh create */
    const lines = bridge.lines.map((l) => ({
      merchandiseId: `gid://shopify/ProductVariant/${l.variantId}`,
      quantity: l.quantity,
    }));
    const codes = bridge.discountCodes || [];
    const data = await withRetry(() => storefrontFetch(
      `mutation CartCreate($lines: [CartLineInput!]!, $codes: [String!]!) {
        cartCreate(input: { lines: $lines, discountCodes: $codes }) {
          cart { id checkoutUrl discountCodes { code applicable } }
          userErrors { message }
        }
      }`,
      { lines, codes },
    ));
    throwUserErrors(data.cartCreate.userErrors, "cartCreate");
    storefrontCart = data.cartCreate.cart;
    console.log("[mod-checkout] Storefront cart created", storefrontCart);

    /* Verify discount actually applied — nếu cartCreate silently drop
       hoặc applicable=false, retry qua cartDiscountCodesUpdate */
    const applied = storefrontCart.discountCodes?.filter((d) => d.applicable).map((d) => d.code) || [];
    const missing = codes.filter((c) => !applied.includes(c));
    if (missing.length) {
      console.log("[mod-checkout] re-apply missing discounts:", missing);
      const reapply = await withRetry(() => storefrontFetch(
        `mutation Codes($cartId: ID!, $codes: [String!]!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {
            cart { id discountCodes { code applicable } }
            userErrors { message }
          }
        }`,
        { cartId: storefrontCart.id, codes },
      ));
      throwUserErrors(reapply.cartDiscountCodesUpdate.userErrors, "discountCodes");
    }

    /* Save cache */
    try {
      sessionStorage.setItem(CART_CACHE_KEY, JSON.stringify({
        cartId: storefrontCart.id,
        signature,
        expires: Date.now() + CART_CACHE_TTL_MS,
      }));
    } catch { /* sessionStorage full / disabled */ }
  }

  /* Fix 4 — Stale cart detection: tab focus lại → check cart cookie có thay đổi không
     (user đổi qty ở tab khác). Nếu khác → reload page để re-render Liquid + bridge. */
  document.addEventListener("visibilitychange", async () => {
    if (document.hidden || !bootstrapDone) return;
    try {
      const res = await fetch(`${Shopify.routes.root}cart.js`, { credentials: "same-origin" });
      const liveCart = await res.json();
      const liveSig = bridgeSignature({
        lines: liveCart.items.map((i) => ({ variantId: i.variant_id, quantity: i.quantity })),
        discountCodes: (liveCart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code),
      });
      const bridge = JSON.parse(document.getElementById("cco-cart-bridge").textContent);
      if (liveSig !== bridgeSignature(bridge)) {
        console.log("[mod-checkout] cart thay đổi tab khác, reload");
        sessionStorage.removeItem(CART_CACHE_KEY);
        window.location.reload();
      }
    } catch { /* network blip, ignore */ }
  });

  const PHONE_REGEX = /^0\d{9,10}$/;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // MST Việt Nam: 10 số (doanh nghiệp/tổ chức), 13 số (10 + 3 số chi nhánh, dấu '-' tùy chọn),
  // hoặc 12 số (mã theo CCCD — cá nhân & hộ kinh doanh theo quy định mới).
  const TAX_CODE_REGEX = /^(\d{10}(-?\d{3})?|\d{12})$/;

  function getField(name) {
    return form.querySelector(`[name="${name}"]`);
  }

  function getValue(name) {
    return (getField(name)?.value || "").trim();
  }

  function setError(name, message) {
    const el = form.querySelector(`[data-error-for="${name}"]`);
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
      getField(name)?.classList.add("is-error");
    } else {
      el.textContent = "";
      el.hidden = true;
      getField(name)?.classList.remove("is-error");
    }
  }

  function clearAllErrors() {
    [
      "email", "fullName", "phone", "address1", "ward", "province",
      "vatTaxCode", "vatCompanyName", "vatCompanyAddress", "vatFullName", "vatConfirm",
    ].forEach((n) => setError(n, ""));
  }

  /* ────── VAT details + lookup ────── */
  // VAT dùng <details>/<summary> + radio Doanh nghiệp/Cá nhân.
  // isVatEnabled = check details.open attribute (native HTML state).
  const vatDetails = document.getElementById("cco-vat-details");
  const vatStatus = form.querySelector("[data-vat-status]");
  const vatTaxInput = getField("vatTaxCode");
  const vatNameInput = getField("vatCompanyName");
  const vatAddressInput = getField("vatCompanyAddress");
  const vatFullNameInput = getField("vatFullName");
  const vatBusinessFields = form.querySelector('[data-cco-vat-fields="business"]');
  const vatIndividualFields = form.querySelector('[data-cco-vat-fields="individual"]');

  function isVatEnabled() {
    return vatDetails?.open === true;
  }

  function getVatType() {
    return form.querySelector('input[name="vatType"]:checked')?.value || "business";
  }

  // Toggle visibility theo radio selection
  form.querySelectorAll('input[name="vatType"]').forEach((radio) => {
    radio.addEventListener("change", () => {
      const type = getVatType();
      if (vatBusinessFields) vatBusinessFields.hidden = type !== "business";
      if (vatIndividualFields) vatIndividualFields.hidden = type !== "individual";
      // Clear VAT errors khi đổi type (fields cũ không còn relevant)
      setError("vatTaxCode", "");
      setError("vatCompanyName", "");
      setError("vatFullName", "");
      setVatStatus("");
    });
  });

  // VietQR lookup chỉ active cho Doanh nghiệp (Cá nhân thường không có data trong DB)
  let vatLookupTimer = null;
  vatTaxInput?.addEventListener("input", () => {
    clearTimeout(vatLookupTimer);
    setVatStatus("");
    setError("vatTaxCode", "");
    if (getVatType() !== "business") return;
    const code = (vatTaxInput.value || "").trim();
    if (!/^\d{10}(-\d{3})?$/.test(code)) {
      return;
    }
    vatLookupTimer = setTimeout(() => lookupVietQR(code), 400);
  });

  // Tabler "rosette-discount-check" — dùng cho state ok (đã tra cứu thành công)
  const VAT_OK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.01 2.011a3.2 3.2 0 0 1 2.113 .797l.154 .145l.698 .698a1.2 1.2 0 0 0 .71 .341l.135 .008h1a3.2 3.2 0 0 1 3.195 3.018l.005 .182v1c0 .27 .092 .533 .258 .743l.09 .1l.697 .698a3.2 3.2 0 0 1 .147 4.382l-.145 .154l-.698 .698a1.2 1.2 0 0 0 -.341 .71l-.008 .135v1a3.2 3.2 0 0 1 -3.018 3.195l-.182 .005h-1a1.2 1.2 0 0 0 -.743 .258l-.1 .09l-.698 .697a3.2 3.2 0 0 1 -4.382 .147l-.154 -.145l-.698 -.698a1.2 1.2 0 0 0 -.71 -.341l-.135 -.008h-1a3.2 3.2 0 0 1 -3.195 -3.018l-.005 -.182v-1a1.2 1.2 0 0 0 -.258 -.743l-.09 -.1l-.697 -.698a3.2 3.2 0 0 1 -.147 -4.382l.145 -.154l.698 -.698a1.2 1.2 0 0 0 .341 -.71l.008 -.135v-1l.005 -.182a3.2 3.2 0 0 1 3.013 -3.013l.182 -.005h1a1.2 1.2 0 0 0 .743 -.258l.1 -.09l.698 -.697a3.2 3.2 0 0 1 2.269 -.944zm3.697 7.282a1 1 0 0 0 -1.414 0l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.32 1.497l2 2l.094 .083a1 1 0 0 0 1.32 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" /></svg>';

  function setVatStatus(text, kind = "") {
    if (!vatStatus) return;
    if (kind === "ok") {
      // Render icon trước text. Safe innerHTML — text từ source nội bộ, không phải user input.
      vatStatus.innerHTML = `${VAT_OK_ICON}<span>${text}</span>`;
    } else {
      vatStatus.textContent = text;
    }
    vatStatus.dataset.kind = kind; // "loading" | "ok" | "error"
  }

  async function lookupVietQR(code) {
    setVatStatus("Đang tra cứu...", "loading");
    // 5s timeout — VietQR API thi thoảng hang, không để user đợi vô hạn.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(
        `https://api.vietqr.io/v2/business/${encodeURIComponent(code)}`,
        { headers: { Accept: "application/json" }, signal: controller.signal },
      );
      clearTimeout(timeoutId);
      if (!res.ok) {
        setVatStatus("Không tra cứu được", "error");
        return;
      }
      const json = await res.json();
      if (json.code !== "00" || !json.data?.name) {
        setVatStatus("Không tìm thấy MST này", "error");
        return;
      }
      // Auto-fill tên + địa chỉ DN (chỉ override khi trống — tôn trọng user nhập tay)
      if (vatNameInput && !vatNameInput.value.trim()) {
        vatNameInput.value = json.data.name;
        setError("vatCompanyName", "");
      }
      if (vatAddressInput && !vatAddressInput.value.trim()) {
        vatAddressInput.value = json.data.address || "";
        setError("vatCompanyAddress", "");
      }
      setVatStatus("Đã tra cứu", "ok");
    } catch (err) {
      clearTimeout(timeoutId);
      console.warn("[vietqr]", err);
      const msg = err.name === "AbortError" ? "Timeout — nhập tay" : "Lỗi mạng — nhập tay";
      setVatStatus(msg, "error");
    }
  }

  function validate() {
    clearAllErrors();
    let firstError = null;

    const email = getValue("email");
    const fullName = getValue("fullName");
    const phone = getValue("phone");
    const address1 = getValue("address1");
    const ward = getValue("ward");
    const province = getValue("province");

    if (!email || !EMAIL_REGEX.test(email)) {
      setError("email", "Email không hợp lệ");
      firstError = firstError || "email";
    }
    if (!fullName) {
      setError("fullName", "Vui lòng nhập họ tên");
      firstError = firstError || "fullName";
    }
    if (!phone || !PHONE_REGEX.test(phone.replace(/\s/g, ""))) {
      setError("phone", "Số điện thoại không hợp lệ (VD: 0901234567)");
      firstError = firstError || "phone";
    }
    if (!address1) {
      setError("address1", "Vui lòng nhập địa chỉ chi tiết");
      firstError = firstError || "address1";
    }
    if (!ward) {
      setError("ward", "Vui lòng nhập Phường/Xã");
      firstError = firstError || "ward";
    }
    if (!province) {
      setError("province", "Vui lòng chọn Tỉnh/Thành");
      firstError = firstError || "province";
    }

    // VAT: validate theo type (business vs individual) khi user bật toggle
    if (isVatEnabled()) {
      const vatType = getVatType();
      const vatConfirmCheckbox = form.querySelector('[name="vatConfirm"]');

      if (vatType === "business") {
        const tax = (vatTaxInput?.value || "").trim();
        const cname = (vatNameInput?.value || "").trim();
        const caddr = (vatAddressInput?.value || "").trim();
        if (!TAX_CODE_REGEX.test(tax)) {
          setError("vatTaxCode", "MST phải 10, 12 hoặc 13 chữ số");
          firstError = firstError || "vatTaxCode";
        }
        if (!cname) {
          setError("vatCompanyName", "Vui lòng nhập tên doanh nghiệp");
          firstError = firstError || "vatCompanyName";
        }
        if (!caddr) {
          setError("vatCompanyAddress", "Vui lòng nhập địa chỉ doanh nghiệp");
          firstError = firstError || "vatCompanyAddress";
        }
      } else {
        // individual
        const fullName = (vatFullNameInput?.value || "").trim();
        const personalTax = (getField("vatPersonalTaxCode")?.value || "").trim();
        if (!fullName) {
          setError("vatFullName", "Vui lòng nhập họ và tên");
          firstError = firstError || "vatFullName";
        }
        // MST cá nhân optional — chỉ validate format nếu user có nhập
        if (personalTax && !TAX_CODE_REGEX.test(personalTax)) {
          setError("vatTaxCode", "MST phải 10, 12 hoặc 13 chữ số");
          firstError = firstError || "vatTaxCode";
        }
      }

      // VAT email — bắt buộc khi mở VAT toggle (gửi hoá đơn điện tử)
      const vatEmail = getValue("vatEmail");
      if (!vatEmail || !EMAIL_REGEX.test(vatEmail)) {
        setError("vatEmail", "Vui lòng nhập email nhận hoá đơn");
        firstError = firstError || "vatEmail";
      }

      if (!vatConfirmCheckbox?.checked) {
        setError("vatConfirm", "Vui lòng tick xác nhận thông tin VAT chính xác");
        firstError = firstError || "vatConfirm";
      }
    }

    return firstError;
  }

  // Split fullname → firstName + lastName theo VN convention
  // (lastName = từ cuối cùng, firstName = phần còn lại).
  function splitName(fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: parts[0] };
    }
    const lastName = parts.pop();
    return { firstName: parts.join(" "), lastName };
  }

  async function submitToStorefront(data) {
    if (!storefrontCart) {
      const reason = bootstrapError ? bootstrapError.message : "cartCreate chưa chạy xong";
      throw new Error(`Storefront cart chưa ready (${reason}). Check Console (F12).`);
    }
    const cartId = storefrontCart.id;
    const { firstName, lastName } = splitName(data.fullName);
    const street = data.address1.trim();
    const ward = data.ward.trim();
    const address1 = ward ? `${street}, ${ward}` : street;

    /* 1. Buyer identity — email/phone/address → prefill checkout */
    const identity = await withRetry(() => storefrontFetch(
      `mutation BuyerIdentity($cartId: ID!, $bi: CartBuyerIdentityInput!) {
        cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $bi) {
          cart { id checkoutUrl }
          userErrors { message }
        }
      }`,
      {
        cartId,
        bi: {
          email: data.email,
          phone: data.phone,
          countryCode: "VN",
          deliveryAddressPreferences: [
            {
              deliveryAddress: {
                firstName,
                lastName,
                address1,
                city: data.province,
                province: data.province,
                country: "VN",
                phone: data.phone,
              },
            },
          ],
        },
      },
    ));
    throwUserErrors(identity.cartBuyerIdentityUpdate.userErrors, "buyerIdentity");

    /* 2. Custom attributes — VN-specific fields → Order Admin Notes */
    const attributes = [
      { key: "Họ tên", value: data.fullName },
      { key: "Số điện thoại", value: data.phone },
      { key: "Phường/Xã", value: data.ward },
      { key: "Tỉnh/Thành", value: data.province },
    ];
    if (data.vat) {
      if (data.vat.type === "business") {
        attributes.push(
          { key: "VAT - Loại", value: "Doanh nghiệp" },
          { key: "VAT - MST", value: data.vat.taxCode },
          { key: "VAT - Tên DN", value: data.vat.companyName },
          { key: "VAT - Địa chỉ", value: data.vat.companyAddress },
        );
      } else {
        attributes.push(
          { key: "VAT - Loại", value: "Cá nhân" },
          { key: "VAT - Họ tên", value: data.vat.fullName },
        );
        if (data.vat.taxCode) attributes.push({ key: "VAT - MST", value: data.vat.taxCode });
      }
      if (data.vat.email) attributes.push({ key: "VAT - Email", value: data.vat.email });
    }
    const attrs = await withRetry(() => storefrontFetch(
      `mutation Attrs($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
          cart { id }
          userErrors { message }
        }
      }`,
      { cartId, attributes },
    ));
    throwUserErrors(attrs.cartAttributesUpdate.userErrors, "attributes");

    /* 3. Note — chỉ user note. VAT info đã ở cart attributes (Additional details),
       không append vào note để tránh duplicate. */
    const userNote = data.note ? data.note.trim() : "";
    if (userNote) {
      const noteRes = await withRetry(() => storefrontFetch(
        `mutation Note($cartId: ID!, $note: String!) {
          cartNoteUpdate(cartId: $cartId, note: $note) {
            cart { id }
            userErrors { message }
          }
        }`,
        { cartId, note: userNote },
      ));
      throwUserErrors(noteRes.cartNoteUpdate.userErrors, "note");
    }

    /* 4. Re-apply discount codes — cartBuyerIdentityUpdate trigger eligibility
       re-check (vd: rule "first-time customer" + có email → ineligible → silent
       remove). Re-apply để Shopify checkout giữ nguyên discount. */
    if (cartDiscountCodes.length) {
      const reapply = await withRetry(() => storefrontFetch(
        `mutation ReapplyCodes($cartId: ID!, $codes: [String!]!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {
            cart { discountCodes { code applicable } }
            userErrors { message }
          }
        }`,
        { cartId, codes: cartDiscountCodes },
      ));
      throwUserErrors(reapply.cartDiscountCodesUpdate.userErrors, "reapplyCodes");
      console.log("[mod-checkout] discount after reapply:", reapply.cartDiscountCodesUpdate.cart.discountCodes);
    }

    return identity.cartBuyerIdentityUpdate.cart.checkoutUrl;
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!bootstrapDone) {
      // Defense in depth — button disabled khi chưa ready, nhưng check lại nếu user
      // bypass disabled state (vd: dev tools).
      showErrorBanner("Đang chuẩn bị giỏ hàng, vui lòng đợi vài giây.");
      return;
    }
    const firstError = validate();
    if (firstError) {
      const el = getField(firstError);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus();
      return;
    }

    const data = {
      email: getValue("email"),
      fullName: getValue("fullName"),
      phone: getValue("phone").replace(/\s/g, ""),
      address1: getValue("address1"),
      ward: getValue("ward"),
      province: getValue("province"),
      note: getValue("note"),
      vat: isVatEnabled()
        ? (function () {
            const type = getVatType();
            const email = getValue("vatEmail");
            if (type === "business") {
              return {
                type: "business",
                taxCode: getValue("vatTaxCode"),
                companyName: getValue("vatCompanyName"),
                companyAddress: getValue("vatCompanyAddress"),
                email,
              };
            }
            return {
              type: "individual",
              fullName: getValue("vatFullName"),
              taxCode: getValue("vatPersonalTaxCode"), // optional
              email,
            };
          })()
        : null,
    };

    hideErrorBanner();
    setSubmitState("loading");

    // ── Email marketing opt-in ──
    // Fire-and-forget POST to Shopify's stock {% form 'customer' %} endpoint
    // (same one newsletter.liquid + footer.liquid hit) so the email lands
    // in the marketing list under the `newsletter` tag. Non-blocking — we
    // intentionally don't await: a slow / failing subscribe must NEVER stall
    // the cart hand-off, and a duplicate-email response is the success case
    // for users already subscribed.
    const wantsMarketing = form.querySelector('[name="email_marketing_optin"]')?.checked;
    if (wantsMarketing && data.email) {
      try {
        const body = new URLSearchParams();
        body.set('form_type', 'customer');
        body.set('utf8', '✓');
        body.set('contact[email]', data.email);
        body.set('contact[tags]', 'newsletter');
        body.set('contact[accepts_marketing]', 'true');
        fetch('/contact', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'text/html',
          },
          body,
        }).catch(() => {}); // swallow — see comment above
      } catch (_) {
        // intentional no-op
      }
    }

    try {
      const checkoutUrl = await submitToStorefront(data);
      console.log("[mod-checkout] redirecting to", checkoutUrl);
      // Xoá cache vì cart đã transition sang checkout — tránh reuse cart đã placed.
      sessionStorage.removeItem(CART_CACHE_KEY);
      window.location.href = checkoutUrl;
    } catch (err) {
      setSubmitState("ready");
      console.error("[mod-checkout]", err);
      showErrorBanner(`Có lỗi xảy ra: ${err.message}. Vui lòng thử lại.`);
    }
  });

  // Real-time clear error khi user gõ
  ["email", "fullName", "phone", "address1", "ward", "province",
   "vatTaxCode", "vatCompanyName", "vatCompanyAddress", "vatFullName",
   "vatPersonalTaxCode", "vatEmail"].forEach(
    (name) => {
      const el = getField(name);
      el?.addEventListener("input", () => { setError(name, ""); updateSubmitButtonState(); });
      el?.addEventListener("change", () => { setError(name, ""); updateSubmitButtonState(); });
    },
  );

  /* ────── Real-time form validation → toggle submit button enabled/disabled ────── */
  function isFormValid() {
    const email = getValue("email");
    const fullName = getValue("fullName");
    const phone = getValue("phone").replace(/\s/g, "");
    if (!email || !EMAIL_REGEX.test(email)) return false;
    if (!fullName) return false;
    if (!phone || !PHONE_REGEX.test(phone)) return false;
    if (!getValue("address1")) return false;
    if (!getValue("ward")) return false;
    if (!getValue("province")) return false;

    if (isVatEnabled()) {
      const vatConfirmCheckbox = form.querySelector('[name="vatConfirm"]');
      if (!vatConfirmCheckbox?.checked) return false;
      const vatEmail = getValue("vatEmail");
      if (!vatEmail || !EMAIL_REGEX.test(vatEmail)) return false;
      if (getVatType() === "business") {
        if (!TAX_CODE_REGEX.test(getValue("vatTaxCode"))) return false;
        if (!getValue("vatCompanyName")) return false;
        if (!getValue("vatCompanyAddress")) return false;
      } else {
        if (!getValue("vatFullName")) return false;
        const personalTax = getValue("vatPersonalTaxCode");
        if (personalTax && !TAX_CODE_REGEX.test(personalTax)) return false;
      }
    }
    return true;
  }

  function updateSubmitButtonState() {
    if (!bootstrapDone) return; // skip khi đang preparing
    if (isFormValid()) {
      submitBtn.classList.remove("is-invalid");
      setSubmitState("ready");
    } else {
      // Invalid — dim visually nhưng VẪN clickable. Click sẽ run validate → highlight fields thiếu.
      submitBtn.classList.add("is-invalid");
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.dataset.defaultLabel || "Chọn phương thức thanh toán";
    }
  }

  // Listeners thêm cho VAT toggle (open/close) + radio type + confirm checkbox
  document.getElementById("cco-vat-details")?.addEventListener("toggle", updateSubmitButtonState);
  form.querySelectorAll('input[name="vatType"]').forEach((r) =>
    r.addEventListener("change", updateSubmitButtonState),
  );
  form.querySelector('[name="vatConfirm"]')?.addEventListener("change", updateSubmitButtonState);

  // Update lần đầu sau bootstrap ready
  const _origSetSubmitState = setSubmitState;
  // Override để init validate state sau khi bootstrap done
  setTimeout(() => updateSubmitButtonState(), 100);

  /* ────── Discount code remove (apply is owned by cart-drawer-coupons.js) ──────

     The MANUAL apply form (input + submit + Enter handler) was previously
     bound here too, but cart-drawer-coupons.js already binds [data-cco-discount-form]
     via MutationObserver and broadcasts `cart:change` on success — exactly the
     flow we want for partial DOM swap. Double-binding here would just race the
     reload pattern (`window.location.reload`) against the broadcast pattern
     and the reload always won. Deleted to leave a single source of truth.

     The REMOVE button (× next to an applied discount line in .cco-totals)
     stays here because cart-drawer's remove handler targets a different
     selector ([data-remove-discount]) and clears ALL codes — we keep
     [data-cco-discount-remove] for per-code removal. Bound via document
     delegation so it survives the cart:change → DOM swap above. */
  setupDiscountRemove();

  function setupDiscountRemove() {
    function setError(msg) {
      const inlineError = document.querySelector("[data-cco-discount-inline-error]");
      if (inlineError && msg) inlineError.textContent = msg;
    }

    // Bundled sections — same protocol cart-drawer-coupons.js uses: dispatch
    // `cart:prepare-bundled-sections` and read back what listeners pushed.
    // Our own listener (declared lower in this file) will add mod-checkout's
    // section id so the cart response includes our HTML.
    function getBundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }),
      );
      return sections;
    }

    async function postDiscount(code) {
      const res = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ discount: code, sections: getBundledSections() }),
      });
      return res.ok ? await res.json() : null;
    }

    async function fetchCart() {
      const res = await fetch(`${Shopify.routes.root}cart.js`, { credentials: "same-origin" });
      return res.ok ? await res.json() : null;
    }

    // Multi-code support: Shopify cho phép multiple discount codes nếu marked
    // "combinable" trong Admin. POST `discount: "CODE1,CODE2"` để set list mới.
    function getCurrentCodes(cart) {
      return (cart?.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
    }

    // Document delegation — handles remove buttons present at load time AND
    // any that arrive later via partial DOM swap.
    document.addEventListener("click", async (e) => {
      const btn = e.target.closest("[data-cco-discount-remove]");
      if (!btn || btn.disabled) return;
      e.preventDefault();

      const codeToRemove = btn.dataset.code;
      btn.disabled = true;
      btn.classList.add("is-removing");
      try {
        const currentCart = await fetchCart();
        const remaining = getCurrentCodes(currentCart).filter(
          (c) => c.toUpperCase() !== (codeToRemove || "").toUpperCase(),
        );
        const cart = await postDiscount(remaining.join(","));
        if (!cart) throw new Error("Lỗi mạng");
        sessionStorage.removeItem("hpw_sf_cart_v1");
        // Broadcast on documentElement → our cart:change handler below picks
        // up cart.sections[ourId] and runs the same partial swap path as
        // apply. No reload.
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { cart, baseEvent: "discount:removed" },
          }),
        );
      } catch (err) {
        btn.disabled = false;
        btn.classList.remove("is-removing");
        setError("Không xoá được mã");
      }
    });
  }

  /* ────── Shipping rates auto-calc ────── */
  const shippingApi = setupShippingRates();

  function setupShippingRates() {
    // Element queries are deferred — done at call time, not init time — so that
    // after a partial DOM swap (cart:change handler below), setShipping picks
    // up the fresh [data-shipping-value]/[data-total-value] nodes. Same reason
    // cartTotalCents is parsed inside setShipping: the server re-renders that
    // attribute when the discount changes the running total.
    let timer = null;

    function trigger() {
      clearTimeout(timer);
      timer = setTimeout(fetchRates, 600);
    }

    ["province", "ward", "address1"].forEach((name) => {
      const el = getField(name);
      el?.addEventListener("change", trigger);
      el?.addEventListener("input", trigger);
    });

    async function fetchRates(retries = 2) {
      const province = getValue("province");
      const ward = getValue("ward");
      const address1 = getValue("address1");
      // ZIP placeholder vì Shopify rate API có thể require — Vietnam thường dùng 100000
      const zip = "100000";

      if (!province) {
        setShipping(null, "Nhập địa chỉ để tính");
        return;
      }

      setShipping(null, "Đang tính phí...");

      try {
        const params = new URLSearchParams({
          "shipping_address[country]": "Vietnam",
          "shipping_address[province]": province,
          "shipping_address[city]": ward || province,
          "shipping_address[zip]": zip,
        });
        if (address1) params.set("shipping_address[address1]", address1);

        const res = await fetch(
          `${Shopify.routes.root}cart/shipping_rates.json?${params}`,
          { credentials: "same-origin", headers: { Accept: "application/json" } },
        );

        if (res.status === 422) {
          // Shopify đang tính async → retry
          if (retries > 0) {
            await new Promise((r) => setTimeout(r, 1500));
            return fetchRates(retries - 1);
          }
          setShipping(null, "Chưa hỗ trợ ship khu vực này");
          return;
        }

        if (!res.ok) {
          setShipping(null, "Không lấy được phí ship");
          return;
        }

        const data = await res.json();
        const rates = data.shipping_rates || [];
        if (!rates.length) {
          setShipping(null, "Chưa có rate cho khu vực này");
          return;
        }

        // Pick cheapest rate
        const cheapest = rates
          .map((r) => ({ ...r, _price: parseFloat(r.price) || 0 }))
          .sort((a, b) => a._price - b._price)[0];

        // Detect free-shipping discount: mã ship để cart total_discount = 0 mà vẫn
        // applicable (mã order/product làm total_discount > 0). Store dùng mã
        // non-combinable nên mỗi lần chỉ 1 mã → heuristic này đủ chính xác.
        let freeShip = false;
        try {
          const c = await fetch(`${Shopify.routes.root}cart.js`, { credentials: "same-origin" }).then((r) => r.json());
          freeShip = c.total_discount === 0 && (c.discount_codes || []).some((d) => d.applicable);
        } catch (_) {}

        setShipping(cheapest, null, freeShip);
        console.log("[shipping] rates:", rates.map((r) => `${r.name}: ${r.price}`), "| freeShip:", freeShip);
      } catch (err) {
        console.warn("[shipping]", err);
        setShipping(null, "Lỗi kết nối");
      }
    }

    function setShipping(rate, fallbackText, freeShip) {
      const shippingValueEl = form.querySelector("[data-shipping-value]");
      const totalValueEl = form.querySelector("[data-total-value]");
      if (!shippingValueEl || !totalValueEl) return;
      const labelEl = form.querySelector("[data-shipping-label]");
      // Read cartTotalCents fresh — discount apply changes this on the server
      // and the new HTML lands with an updated data-cart-total attribute.
      const cartTotalCents = parseInt(totalValueEl.dataset.cartTotal, 10) || 0;

      if (!rate) {
        if (labelEl) labelEl.textContent = "Phí vận chuyển";
        shippingValueEl.textContent = fallbackText || "--";
        shippingValueEl.classList.add("cco-total-muted");
        shippingValueEl.classList.remove("cco-shipping-free");
        totalValueEl.textContent = formatVnd(cartTotalCents);
        return;
      }

      // Có mã free-shipping → hiển thị "Miễn phí" (xanh) và KHÔNG cộng phí ship
      // vào tổng. Shopify chỉ trừ phí ship ở checkout cuối; /cart/shipping_rates.json
      // vẫn trả phí gốc nên phải tự override cho khớp checkout chuẩn.
      if (freeShip) {
        if (labelEl) labelEl.textContent = rate.name;
        shippingValueEl.textContent = "Miễn phí";
        shippingValueEl.classList.remove("cco-total-muted");
        shippingValueEl.classList.add("cco-shipping-free");
        totalValueEl.textContent = formatVnd(cartTotalCents);
        return;
      }

      // Shopify returns price as string like "50000.00" (VND zero-decimal,
      // value đã ở đơn vị VND). Cart total ở cents nên * 100 để cộng.
      const rateVnd = Math.round(parseFloat(rate.price));
      const rateCents = rateVnd * 100;
      const newTotalCents = cartTotalCents + rateCents;

      // Bên trái: rate name (vd "Vận chuyển tiêu chuẩn (Gửi hàng sau 24h)")
      // Bên phải: chỉ giá
      if (labelEl) labelEl.textContent = rate.name;
      shippingValueEl.textContent = formatVnd(rateCents);
      shippingValueEl.classList.remove("cco-total-muted", "cco-shipping-free");
      totalValueEl.textContent = formatVnd(newTotalCents);
    }

    function formatVnd(cents) {
      // cents (Shopify internal) → VND display với dấu chấm thousand separator
      const vnd = Math.round(cents / 100);
      return vnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }

    // Expose a refetch hook for the cart:change handler: after a partial swap
    // the server-rendered total has been clobbered with the pre-shipping total
    // — calling refetch re-runs the rate API and overlays it back on top.
    return { refetch: fetchRates };
  }

  /* ────── Cart change handler — partial DOM swap (no reload).

     Mirrors the cart-drawer pattern at theme.js#L1701:
     1. Push this section's id via `cart:prepare-bundled-sections` so the
        /cart/update.js POST includes us in its `sections` map.
     2. On `cart:change`, take cart.sections[ourId], parse it, and swap the
        three blocks that depend on cart state — .cco-lines, .cco-discount-area,
        .cco-totals. The submit button (.cco-confirm) is intentionally NOT
        swapped: its label + disabled state are owned by the captured
        `submitBtn` const + setSubmitState/Storefront bootstrap pipeline,
        and a re-rendered button would land with default Liquid state that
        contradicts whatever the form pipeline last set.
     3. After the swap, re-trigger the shipping rate fetch so the cart-total
        re-includes the user's previously calculated shipping cost (the
        server doesn't know about it — its rendered total is pre-shipping). */
  const sectionRoot = form.closest('[id^="shopify-section-"]');
  const sectionId = sectionRoot ? sectionRoot.id.replace(/^shopify-section-/, "") : null;

  if (sectionId) {
    document.documentElement.addEventListener("cart:prepare-bundled-sections", (event) => {
      event.detail.sections.push(sectionId);
    });
  }

  document.documentElement.addEventListener("cart:change", (event) => {
    const sectionHtml = sectionId ? event.detail?.cart?.sections?.[sectionId] : null;

    // Fallback: if for any reason the bundled section HTML didn't make it back
    // (older callers, race, network error), keep the old reload behavior so
    // the user never sees stale totals.
    if (!sectionHtml) {
      window.location.reload();
      return;
    }

    try {
      const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
      const swap = (selector) => {
        const cur = form.querySelector(selector);
        const fresh = doc.querySelector(selector);
        if (cur && fresh) cur.replaceWith(fresh);
      };

      swap(".cco-lines");
      swap(".cco-discount-area");
      swap(".cco-totals");

      // Server-rendered totals are pre-shipping. If the user already had a
      // shipping rate displayed, re-fetch to overlay it. Refetch is a no-op
      // when no province is filled yet.
      shippingApi?.refetch();
    } catch (err) {
      console.error("[mod-checkout] partial render failed, falling back to reload:", err);
      window.location.reload();
    }
  });

  /* ────── Goong autocomplete ────── */
  setupGoongAutocomplete();

  function setupGoongAutocomplete() {
    const apiKey = window.HPW_GOONG_KEY;
    // Theme snippet 'input' không có data-goong-input attr → query by name
    const input = form.querySelector('[name="address1"]');
    const dropdown = form.querySelector("[data-goong-dropdown]");
    if (!input || !dropdown) return;

    if (!apiKey) {
      console.log(
        "[mod-checkout] Goong API key chưa set. Theme Editor → Custom Checkout section → Goong Maps API Key.",
      );
      return;
    }

    let timer = null;
    let lastPredictions = [];

    input.addEventListener("input", () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 3) {
        hideDropdown();
        return;
      }
      timer = setTimeout(() => fetchSuggestions(q), 250);
    });

    input.addEventListener("blur", () => {
      // Delay để click vào dropdown item kịp register
      setTimeout(hideDropdown, 200);
    });

    input.addEventListener("focus", () => {
      if (lastPredictions.length && input.value.trim().length >= 3) {
        showDropdown();
      }
    });

    async function fetchSuggestions(q) {
      try {
        const url = `https://rsapi.goong.io/Place/AutoComplete?api_key=${encodeURIComponent(apiKey)}&input=${encodeURIComponent(q)}&limit=5`;
        const res = await fetch(url);
        if (!res.ok) {
          console.warn("[goong] HTTP", res.status);
          return;
        }
        const data = await res.json();
        lastPredictions = data.predictions || [];
        renderSuggestions(lastPredictions);
      } catch (err) {
        console.warn("[goong] fetch error", err);
      }
    }

    function renderSuggestions(predictions) {
      if (!predictions.length) {
        hideDropdown();
        return;
      }
      dropdown.innerHTML = predictions
        .map(
          (p, i) => `
          <button type="button" class="mod-checkout__autocomplete-item" data-index="${i}">
            <span class="mod-checkout__ac-main">${escapeHtml(p.structured_formatting?.main_text || p.description)}</span>
            <span class="mod-checkout__ac-secondary">${escapeHtml(p.structured_formatting?.secondary_text || "")}</span>
          </button>
        `,
        )
        .join("");
      showDropdown();

      dropdown.querySelectorAll("button").forEach((btn) => {
        btn.addEventListener("mousedown", (e) => {
          // mousedown thay vì click — fire trước input blur
          e.preventDefault();
          const i = parseInt(btn.dataset.index, 10);
          const p = predictions[i];
          if (!p) return;
          applySuggestion(p);
          hideDropdown();
        });
      });
    }

    async function applySuggestion(p) {
      // Optimistic UI: fill ngay với main_text (place name) trước khi Place Detail
      // trả về — user thấy phản hồi nhanh. Sau đó override với data chính xác.
      input.value = p.structured_formatting?.main_text || p.description || "";
      setError("address1", "");

      // Goong Place Detail API trả compound { commune, district, province } structured —
      // reliable hơn parse text từ description. Spec: https://docs.goong.io/rest/place/#detail
      try {
        const detailUrl = `https://rsapi.goong.io/Place/Detail?api_key=${encodeURIComponent(apiKey)}&place_id=${encodeURIComponent(p.place_id)}`;
        const res = await fetch(detailUrl);
        if (!res.ok) return;
        const json = await res.json();
        const r = json.result;
        if (!r) return;

        const commune = (r.compound?.commune || "").trim();
        const district = (r.compound?.district || "").trim();
        const province = (r.compound?.province || "").trim();
        const formatted = r.formatted_address || p.description || "";

        // Set Phường/Xã (commune)
        const wardInput = form.querySelector('[name="ward"]');
        if (wardInput && commune) {
          wardInput.value = commune;
          setError("ward", "");
        }

        // Set Tỉnh/Thành — match province name với option có sẵn
        const provinceSelect = form.querySelector('[name="province"]');
        if (provinceSelect && province) {
          const options = Array.from(provinceSelect.options).map((o) => o.value);
          const matched = options.find(
            (opt) => opt && (opt === province || opt.includes(province) || province.includes(opt)),
          );
          if (matched) {
            provinceSelect.value = matched;
            setError("province", "");
          }
        }

        // Strip commune + district + province khỏi formatted address → còn lại street
        let parts = formatted.split(",").map((s) => s.trim()).filter(Boolean);
        parts = parts.filter((part) => {
          if (commune && (part === commune || part.includes(commune))) return false;
          if (district && (part === district || part.includes(district))) return false;
          if (province && (part === province || part.includes(province))) return false;
          return true;
        });
        if (parts.length) input.value = parts.join(", ");
      } catch (err) {
        console.warn("[goong] Place Detail fetch failed", err);
        // Giữ optimistic value đã fill ở đầu function
      }

      // .value gán bằng JS không tự phát event — dispatch 'change' để các
      // pipeline đang lắng nghe (auto-calc phí ship + validate nút submit)
      // nhận giá trị mới. Dùng 'change' chứ không 'input' vì 'input' trên
      // address1 sẽ re-trigger chính autocomplete này (mở lại dropdown).
      ["address1", "ward", "province"].forEach((name) => {
        getField(name)?.dispatchEvent(new Event("change", { bubbles: true }));
      });

      lastPredictions = []; // chặn focus() mở lại dropdown với gợi ý cũ
      input.focus();
    }

    function showDropdown() {
      dropdown.hidden = false;
    }

    function hideDropdown() {
      dropdown.hidden = true;
    }

    function escapeHtml(s) {
      return String(s ?? "").replace(/[&<>"']/g, (c) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
      })[c]);
    }
  }
})();
